import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useFragment } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator/ClubStackNavigator";
import { Button } from "@whoosh/design-system/src/components/controls";
import { LoadingSpinner } from "@whoosh/design-system/src/components/display/Loader/LoadingSpinner";
import { CloseIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { PanelHeader } from "@whoosh/design-system/src/components/layout/PanelHeader";
import {
  VStack,
  Text,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { goToTransferOwnershipStepAtom } from "../../atoms/actions/goToTransferOwnershipStepAtom";
import { CancelBooking_Booking$key } from "./__generated__/CancelBooking_Booking.graphql";
import { useCancelBooking } from "./hooks/useCancelBooking";

type Props = {
  bookingRef: CancelBooking_Booking$key;
  onClose: () => void;
  currentUserClientId: string;
};

export const CancelBooking = ({
  bookingRef,
  onClose,
  currentUserClientId,
}: Props) => {
  const goToTransferOwnershipStep = useSetAtom(goToTransferOwnershipStepAtom);
  const [cancelBooking, isCancelingBooking] = useCancelBooking();
  const booking = useFragment(CancelBookingFragmentString, bookingRef);

  const isCurrentUserInBooking = booking?.rounds?.edges?.some(
    (e) => e?.node?.player?.id === currentUserClientId
  );

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList, "Home">>();

  const handleCancelBooking = () => {
    cancelBooking(
      {
        startSlotId: booking?.startSlot?.id ?? "",
        bookingId: booking?.id ?? "",
        notifyMember: true,
      },
      () => {
        onClose();
        navigation.navigate("Home", { screen: "MainHome" });
      }
    );
  };

  return (
    <>
      {isCancelingBooking ? (
        <Box alignItems="center" justifyContent="center" flex={1}>
          <LoadingSpinner />
        </Box>
      ) : (
        <VStack flex={1} backgroundColor="surface-primary">
          <PanelHeader
            paddingVariant="medium"
            hideBottomBorder
            header=""
            outerLeftComponent={
              <Button
                testID="modal-close-btn"
                variant="secondary"
                accessibilityLabel={t`Close button`}
                onPress={onClose}
                leftIcon={<CloseIcon />}
              />
            }
          />
          <VStack
            flex={1}
            backgroundColor="surface-primary"
            justifyContent="space-between"
            px={6}
            paddingBottom={6}
          >
            <VStack gap={4} marginTop={1}>
              <Text variant={"mainTitle2"}>{t`Cancel Booking`}</Text>
              <Text variant="mainBaseMedium" color="text-primary">
                {t`Are you sure you want to cancel this booking?`}
              </Text>
            </VStack>

            {/* buttons */}
            <VStack gap={2}>
              <Button
                variant="secondary"
                accessibilityLabel={t`Cancel Entire Booking button`}
                onPress={handleCancelBooking}
              >{t`Cancel Entire Booking`}</Button>
              {isCurrentUserInBooking ? (
                <Button
                  accessibilityLabel={t`Just Remove Me From Booking button`}
                  variant="main"
                  onPress={goToTransferOwnershipStep}
                >{t`Just Remove Me From Booking`}</Button>
              ) : null}
            </VStack>
          </VStack>
        </VStack>
      )}
    </>
  );
};

const CancelBookingFragmentString = graphql`
  fragment CancelBooking_Booking on Booking {
    id
    startSlot {
      id
    }
    rounds {
      edges {
        node {
          player {
            id
          }
        }
      }
    }
  }
`;
